import React from 'react';
import { useTranslation } from 'react-i18next';
import { AgentState, CallState, UnavailableCode } from '../../types';
import { AuthUser } from '../../services/api/auth.types';
import { AgentStateSelector } from '../settings/AgentStateSelector';
import { AgentWidgetSettingsMenu } from './AgentWidgetSettingsMenu';
import { CallHistoryButton } from '../activity-log/CallHistoryDrawer';
import { isAgentStateSelectorEnabled } from './widget-view-routing.util';
import settingsStyles from './AgentWidgetSettings.module.css';

interface AgentWidgetHeaderActionsProps {
  agentState: AgentState;
  unavailableCodes: UnavailableCode[];
  callState: CallState;
  user: AuthUser | null | undefined;
  isConnected: boolean;
  settingsRef: React.RefObject<HTMLDivElement>;
  showSettingsMenu: boolean;
  onToggleSettingsMenu: () => void;
  onCloseSettingsMenu: () => void;
  onOpenMicSettings: () => void;
  onOpenProfile: () => void;
  onOpenCallHistory: () => void;
  onStateChange: (state: string, reasonCode?: string) => Promise<void>;
  onLogout: () => Promise<void>;
}

export function AgentWidgetHeaderActions({
  agentState,
  unavailableCodes,
  callState,
  user,
  isConnected,
  settingsRef,
  showSettingsMenu,
  onToggleSettingsMenu,
  onCloseSettingsMenu,
  onOpenMicSettings,
  onOpenProfile,
  onOpenCallHistory,
  onStateChange,
  onLogout,
}: AgentWidgetHeaderActionsProps) {
  const { t } = useTranslation();

  return (
    <>
      <AgentStateSelector
        currentState={agentState}
        onStateChange={onStateChange}
        disabled={!isAgentStateSelectorEnabled(callState.status)}
        unavailableCodes={unavailableCodes}
      />
      <CallHistoryButton onClick={onOpenCallHistory} />
      <div className={settingsStyles.settingsContainer} ref={settingsRef}>
        <button
          className={`${settingsStyles.settingsButton} ${
            isConnected ? settingsStyles.settingsConnected : settingsStyles.settingsDisconnected
          }`}
          onClick={onToggleSettingsMenu}
          title={
            isConnected ? t('agent:menu.connectedSettings') : t('agent:menu.disconnectedSettings')
          }
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
          </svg>
        </button>
        {showSettingsMenu && (
          <AgentWidgetSettingsMenu
            user={user}
            onOpenMicSettings={onOpenMicSettings}
            onOpenProfile={onOpenProfile}
            onCloseSettingsMenu={onCloseSettingsMenu}
            onLogout={onLogout}
          />
        )}
      </div>
    </>
  );
}

export default AgentWidgetHeaderActions;
